import { isMapFeatureFlagEnabled } from '@helpers/events';
import type { IEvent } from '@interfaces/event';
import type { IBrandingEntity, IBrandingProperty, IMap } from '@interfaces/map';
import type { IBrandingEntityValues } from '@interfaces/rooms';

/** Map feature flag the whole entity editor hangs off; enabled unless an experience turns it off. */
export const UNIVERSAL_BRANDING_FLAG_SLUG = 'UNIVERSAL_BRANDING_UE';

/**
 * Control a property's declared `type` asks for.
 *
 * `unsupported` is the answer for a type this hub version has no widget for —
 * a newer build may declare one, and guessing a widget would write a value UE
 * then refuses.
 */
export type BrandingPropertyWidget =
  | 'colorPicker'
  | 'numberField'
  | 'integerField'
  | 'toggle'
  | 'textField'
  | 'urlField'
  | 'pathField'
  | 'select'
  | 'unsupported';

/** Why a value cannot be saved; the row maps this to a message. */
export type BrandingValueErrorCode =
  | 'color'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'enum'
  | 'assetPath'
  | 'textureUrl';

const COLOR_PATTERN = /^#?([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const DECIMAL_PATTERN = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;
const INTEGER_PATTERN = /^[+-]?\d+$/;
const HTTP_URL_PATTERN = /^https?:\/\/\S+$/i;

/**
 * Picks the control for a catalogue property.
 *
 * `assetPath` is a closed set only when the build declared one, so it resolves
 * to a dropdown or a free path field depending on `allowedValues`.
 *
 * @param property - Catalogue property being rendered.
 * @returns The widget to render, or `unsupported` for an unknown type.
 */
export const resolveBrandingPropertyWidget = (
  property: Pick<IBrandingProperty, 'type' | 'allowedValues'>,
): BrandingPropertyWidget => {
  switch (property.type) {
    case 'color':
      return 'colorPicker';
    case 'number':
      return 'numberField';
    case 'integer':
      return 'integerField';
    case 'boolean':
      return 'toggle';
    case 'text':
      return 'textField';
    case 'textureUrl':
      return 'urlField';
    case 'enum':
      return 'select';
    case 'assetPath':
      return property.allowedValues && property.allowedValues.length > 0
        ? 'select'
        : 'pathField';
    default:
      return 'unsupported';
  }
};

/**
 * Expands a hex colour to the canonical form UE parses.
 *
 * Accepts `#RGB`, `#RGBA`, `RRGGBB`, `#RRGGBB` and `#RRGGBBAA`, with or without
 * the leading `#`; shorthand digits are doubled, alpha is kept when given.
 *
 * @param raw - Value as typed or emitted by the picker.
 * @returns The normalized `#RRGGBB` / `#RRGGBBAA` value, or `null` when unparseable.
 */
export const normalizeBrandingColorValue = (raw: string): string | null => {
  const trimmed = raw.trim();
  const match = COLOR_PATTERN.exec(trimmed);
  if (!match) {
    return null;
  }

  const digits = match[1];
  if (digits.length === 3 || digits.length === 4) {
    return `#${digits
      .split('')
      .map((digit) => `${digit}${digit}`)
      .join('')}`;
  }

  return `#${digits}`;
};

/**
 * Checks a value against what the property's type accepts.
 *
 * An empty value is never an error — it means the property is cleared, which
 * restores what the level authored.
 *
 * @param property - Catalogue property the value belongs to.
 * @param value - Value as held by the editor.
 * @returns The failing rule, or `null` when the value is safe to save.
 */
export const validateBrandingPropertyValue = (
  property: Pick<IBrandingProperty, 'type' | 'allowedValues'>,
  value: string,
): BrandingValueErrorCode | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  switch (property.type) {
    case 'color':
      return normalizeBrandingColorValue(trimmed) ? null : 'color';
    case 'number':
      return DECIMAL_PATTERN.test(trimmed) ? null : 'number';
    case 'integer':
      // A fractional value is refused by UE per property, so it is reported
      // rather than rounded — rounding would silently save a different value.
      return INTEGER_PATTERN.test(trimmed) ? null : 'integer';
    case 'boolean':
      return trimmed === 'true' || trimmed === 'false' ? null : 'boolean';
    case 'enum':
      return (property.allowedValues ?? []).includes(trimmed) ? null : 'enum';
    case 'textureUrl':
      return HTTP_URL_PATTERN.test(trimmed) ? null : 'textureUrl';
    case 'assetPath':
      if (property.allowedValues && property.allowedValues.length > 0) {
        return property.allowedValues.includes(trimmed) ? null : 'assetPath';
      }
      return trimmed.startsWith('/') ? null : 'assetPath';
    default:
      // An unsupported type is rendered read-only, so nothing can be invalid.
      return null;
  }
};

/**
 * Normalizes the value the editor holds into the one that gets stored.
 *
 * @param property - Catalogue property the value belongs to.
 * @param value - Value as held by the editor.
 * @returns The value to store, or `''` when the property is cleared.
 */
export const toStoredBrandingValue = (
  property: Pick<IBrandingProperty, 'type' | 'allowedValues'>,
  value: string,
): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (property.type === 'color') {
    return normalizeBrandingColorValue(trimmed) ?? trimmed;
  }

  return trimmed;
};

/**
 * Builds the `brandingEntities` body for one entity.
 *
 * The backend replaces the whole property map of every entity it is given, so
 * the complete set of stored values is sent — a cleared property is omitted,
 * which is what restores the authored value.
 *
 * @param entityId - Entity being saved.
 * @param values - Every property the editor holds for that entity.
 * @returns The request body fragment, carrying that entity alone.
 */
export const buildBrandingEntityValues = (
  entityId: string,
  values: Record<string, string>,
): IBrandingEntityValues => {
  const properties: Record<string, string> = {};

  Object.entries(values).forEach(([propertyId, value]) => {
    if (value) {
      properties[propertyId] = value;
    }
  });

  return { [entityId]: properties };
};

/**
 * Builds the single payload that moves UE from one configuration to another.
 *
 * Every entity the outgoing configuration overrode but the incoming one does
 * not is sent as an empty map, which restores the authored values — per-entity
 * replace cannot unset an entity the payload never names.
 *
 * @param previousValues - Values of the configuration being left.
 * @param nextValues - Values of the configuration being activated.
 * @returns The union to send as one `BRANDING_UPDATED`.
 */
export const buildBrandingSwitchEntities = (
  previousValues: IBrandingEntityValues | undefined,
  nextValues: IBrandingEntityValues | undefined,
): IBrandingEntityValues => {
  const switchEntities: IBrandingEntityValues = {};

  Object.keys(previousValues ?? {}).forEach((entityId) => {
    switchEntities[entityId] = {};
  });

  Object.entries(nextValues ?? {}).forEach(([entityId, properties]) => {
    switchEntities[entityId] = properties;
  });

  return switchEntities;
};

/**
 * Reads the brandable entities the current map declares.
 *
 * @param map - Map of the room being edited.
 * @returns The catalogue entries, or an empty list when the build declares none.
 */
export const getBrandingEntityCatalogue = (
  map: IMap | null | undefined,
): IBrandingEntity[] => {
  const entities = map?.brandingEntities;
  if (!Array.isArray(entities)) {
    return [];
  }

  return entities.filter((entity) => !!entity?.entityId);
};

/**
 * Tells whether the entity editor may be shown for the current room.
 *
 * @param currentEvent - Experience holding the per-map flag overrides.
 * @param map - Map of the room being edited.
 * @returns Whether the flag is on and the build declares something to brand.
 */
export const isBrandingEntityEditorAvailable = (
  currentEvent: IEvent | null | undefined,
  map: IMap | null | undefined,
): boolean => {
  if (getBrandingEntityCatalogue(map).length === 0) {
    return false;
  }

  return isMapFeatureFlagEnabled(
    currentEvent,
    map,
    UNIVERSAL_BRANDING_FLAG_SLUG,
  );
};
